import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Card, Field } from "@/components/ui-kit";
import { useAuth } from "@/auth";
import { api, formatError } from "@/api";
import { Mail, Shield, LogOut, Crown, KeyRound, AlertTriangle, Trash2, X, Newspaper } from "lucide-react";

const CONFIRM_WORD = "POISTA";

export default function Settings() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [newsletter, setNewsletter] = useState(!!user.newsletter_opt_in);
  const [savingNews, setSavingNews] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const toggleNewsletter = async (opt_in) => {
    setNewsletter(opt_in);
    setSavingNews(true);
    try {
      const { data } = await api.put("/profile/newsletter", { opt_in });
      setUser(data);
      toast.success(opt_in ? "Tilasit uutiskirjeen." : "Uutiskirjeen tilaus peruttu.");
    } catch (err) {
      setNewsletter(!opt_in);
      toast.error(formatError(err.response?.data?.detail) || "Tallennus epäonnistui.");
    } finally {
      setSavingNews(false);
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setShowDelete(false);
    setConfirmText("");
  };

  const doDelete = async () => {
    if (confirmText.trim() !== CONFIRM_WORD || deleting) return;
    setDeleting(true);
    try {
      await api.delete("/auth/account", { data: { confirm: CONFIRM_WORD } });
      setUser(false);
      toast.success("Tilisi on poistettu.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Tilin poistaminen epäonnistui.");
      setDeleting(false);
    }
  };

  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-4 py-4">
      <div className="h-10 w-10 rounded-full bg-[#E9EAEC] flex items-center justify-center text-[#292827]"><Icon size={17} strokeWidth={1.75} /></div>
      <div>
        <div className="text-[13px] text-[#968F85]">{label}</div>
        <div className="font-medium text-[#292827]">{value}</div>
      </div>
    </div>
  );

  return (
    <div data-testid="settings-page" className="max-w-2xl space-y-5">
      <h3 className="text-2xl font-semibold tracking-display text-[#292827]">Asetukset</h3>

      <Card className="px-8 divide-y divide-[#E6E1D9]">
        <Row icon={Mail} label="Sähköposti" value={user.email} />
        <Row icon={Shield} label="Rooli" value={user.role === "creator" ? "Sisällöntuottaja" : "Yritys"} />
        <Row icon={KeyRound} label="Kirjautumistapa" value={user.auth_provider === "google" ? "Google" : "Sähköposti"} />
      </Card>

      <Card className="p-8">
        <div className="flex items-center gap-2 text-sm font-medium text-[#292827]"><Crown size={16} strokeWidth={1.75} /> Jäsenyys</div>
        <p className="mt-2 text-[15px] text-[#6E675C]">Hallitse Pro-jäsenyyttäsi ja katso edut.</p>
        <Btn variant="secondary" className="mt-5" onClick={() => navigate("/dashboard/tilaus")} data-testid="premium-btn">Jäsenyys ja lasku</Btn>
      </Card>

      <Card className="p-8">
        <div className="flex items-center gap-2 text-sm font-medium text-[#292827]"><Newspaper size={16} strokeWidth={1.75} /> Uutiskirje</div>
        <p className="mt-2 text-[15px] text-[#6E675C] leading-relaxed">Saat ajankohtaiset uutiset ja päivitykset Creatorista sähköpostiisi. Voit peruuttaa milloin tahansa.</p>
        <label className="mt-5 flex items-center gap-3 text-[15px] text-[#292827] cursor-pointer select-none">
          <input type="checkbox" checked={newsletter} disabled={savingNews}
            onChange={(e) => toggleNewsletter(e.target.checked)}
            data-testid="settings-newsletter-toggle"
            className="h-4 w-4 rounded border-[#C7C0B5] accent-[#292827] shrink-0" />
          <span>Tilaan Creatorin uutiskirjeen</span>
        </label>
      </Card>

      <Card className="p-8">
        <Btn variant="outline" onClick={doLogout} data-testid="settings-logout"><LogOut size={18} strokeWidth={1.75} /> Kirjaudu ulos</Btn>
      </Card>

      {/* Vaaravyöhyke */}
      <Card className="p-8 border border-[#F0D4D4] bg-[#FCF6F6]" data-testid="danger-zone">
        <div className="flex items-center gap-2 text-sm font-medium text-[#B4362F]"><AlertTriangle size={16} strokeWidth={1.75} /> Vaaravyöhyke</div>
        <p className="mt-2 text-[15px] text-[#6E675C] leading-relaxed">
          Voit poistaa käyttäjätilisi itse. Tilin poistaminen on pysyvää eikä sitä voi peruuttaa.
        </p>
        <button
          onClick={() => setShowDelete(true)}
          data-testid="open-delete-account"
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#D9534F] bg-white px-6 py-3 text-[15px] font-medium text-[#B4362F] hover:bg-[#B4362F] hover:text-white transition-colors">
          <Trash2 size={18} strokeWidth={1.75} /> Poista tili
        </button>
      </Card>

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="delete-account-modal" onClick={closeDelete}>
          <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-[#B4362F]"><AlertTriangle size={20} strokeWidth={1.75} /></div>
              <button onClick={closeDelete} data-testid="close-delete-modal" className="text-[#968F85] hover:text-[#292827] transition-colors"><X size={20} /></button>
            </div>
            <h4 className="mt-3 text-2xl font-semibold tracking-display text-[#292827]">Poista tili</h4>
            <p className="mt-3 text-[15px] text-[#6E675C] leading-relaxed">
              Tilin poistaminen on pysyvää. Profiilisi ja henkilötietosi poistetaan eikä toimintoa voi peruuttaa.
            </p>
            <Field
              label={`Kirjoita ${CONFIRM_WORD} vahvistaaksesi`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_WORD}
              autoFocus
              data-testid="delete-confirm-input"
              className="mt-6"
            />
            <div className="mt-6 flex gap-3">
              <Btn variant="outline" onClick={closeDelete} disabled={deleting} data-testid="cancel-delete" className="flex-1">Peruuta</Btn>
              <button
                onClick={doDelete}
                disabled={confirmText.trim() !== CONFIRM_WORD || deleting}
                data-testid="confirm-delete-account"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#B4362F] px-6 py-3 text-[15px] font-medium text-white hover:bg-[#93261F] transition-colors disabled:opacity-40 disabled:pointer-events-none">
                <Trash2 size={18} strokeWidth={1.75} /> {deleting ? "Poistetaan…" : "Poista tili"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
